import React, { useCallback, type FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { useNotesActions } from '@confluence/notes-util/entry-points/NotesStore';
import { START_TOUCH } from '@confluence/navdex';
import { useMatchRoute } from '@confluence/route-manager/entry-points/RouteState';
import { useFlyout } from '@confluence/flyout-store';
import { LoadableLazy } from '@confluence/loadable';

import { NotesIconComponent } from '../GlobalNavigationIcons';
import { ContentMenuLoadingState } from '../../ContentMenu/ContentMenuStates';

import type { GlobalItemProps } from './globalItemProps';
import { useGlobalItemVisibility } from './useGlobalItemVisibility';
import { UNIVERSAL_CREATE_NOTE_SHORTCUT, GeneralShortcutListener } from '@confluence/shortcuts';
import { useNotesData } from '@confluence/notes-util/entry-points/hooks/useNotesData';

const NotesMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-NotesMenu" */
				'../../ContentMenu/NotesMenu'
			)
		).NotesMenu,
	loading: () => <ContentMenuLoadingState />,
});

const i18n = defineMessages({
	notes: {
		id: 'side-navigation.global.notes',
		defaultMessage: 'Notes',
		description: "Label text for a flyout item title that opens a menu of the user's notes.",
	},
});

const MENU_ID = 'notes';

export const NotesItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const matchRoute = useMatchRoute();
	const { isFlyoutOpen, openFlyout, closeFlyout } = useFlyout(MENU_ID);
	const { setActiveNoteId } = useNotesActions();

	const onNoteClick = useCallback(
		(noteId: string) => {
			setActiveNoteId(noteId);
			closeFlyout();
		},
		[closeFlyout, setActiveNoteId],
	);

	const onClickNavItem = useCallback(() => {
		if (!isFlyoutOpen) {
			openFlyout();
		} else {
			closeFlyout();
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'notes',
				attributes: {
					selectedItemPageContext: matchRoute?.name,
					navigationLayer: 'global',
					navVersion: '4',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, matchRoute?.name, isFlyoutOpen, openFlyout, closeFlyout]);

	const onNoteCreate = useCallback(() => {
		if (isFlyoutOpen) {
			closeFlyout();
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'notesMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createNote',
				attributes: {
					selectedItemPageContext: matchRoute?.name,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, matchRoute?.name, isFlyoutOpen, closeFlyout]);

	const { createNote } = useNotesData();

	const handleNoteCreation = useCallback(async () => {
		// Currently, we are sending in an empty adf body for note creation, but may need to replace this
		// when the BE is capable of handling a null body

		const { data } = await createNote({
			variables: { body: '{"version":1,"type":"doc","content":[]}' },
		});

		setActiveNoteId(data?.createNote?.note?.ari);
		onNoteCreate?.();
	}, [createNote, setActiveNoteId, onNoteCreate]);

	const onOpenChange = useCallback(
		(newIsOpen: boolean) => {
			if (!newIsOpen) {
				isFlyoutOpen && closeFlyout();
			} else {
				openFlyout();
			}
		},
		[isFlyoutOpen, closeFlyout, openFlyout],
	);

	const shouldHide = useGlobalItemVisibility(
		MENU_ID,
		isFlyoutOpen,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<FlyoutMenuItem onOpenChange={onOpenChange} isOpen={isFlyoutOpen}>
			<FlyoutMenuItemTrigger elemBefore={NotesIconComponent} onClick={onClickNavItem}>
				<FormattedMessage {...i18n.notes} />
			</FlyoutMenuItemTrigger>

			{/* autofocus disabled to keep focus on filter field when opened  */
			/*eslint-disable-next-line jsx-a11y/no-autofocus*/}
			<FlyoutMenuItemContent onClose={() => isFlyoutOpen && closeFlyout()} autoFocus={false}>
				<NotesMenuLoader onNoteClick={onNoteClick} onNoteCreate={onNoteCreate} />
			</FlyoutMenuItemContent>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_CREATE_NOTE_SHORTCUT}
				listener={handleNoteCreation}
				isAppNavigationShortcut
			/>
		</FlyoutMenuItem>
	);
};
